import { useRef, useState } from 'react';

import { errorKey, type AuthApi, type Session } from '../auth/api.ts';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { avatarUrl } from './api.ts';
import { PresetAvatar } from './PresetAvatar';

export function AvatarPicker(
  { open, api, session, presets, current, accountUrl, onClose, onDone }:
  {
    open: boolean;
    api: AuthApi;
    session: Session;
    presets: string[];
    current: string | null;
    accountUrl: string;
    onClose(): void;
    onDone(): void;
  },
) {
  const { t } = useI18n();
  const file = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr('');
    try { await fn(); onDone(); } catch (e) { setErr(t(errorKey(e))); } finally { setBusy(false); }
  }

  const uploaded = avatarUrl(current, accountUrl);

  return (
    <Sheet open={open} title={t('profile.changeAvatar')} onClose={onClose}>
      {err && <p className="field__err" role="alert">{err}</p>}

      <div className="avatars">
        {presets.map((p) => (
          <button key={p} type="button" className="avatars__one"
                  aria-pressed={p === current} disabled={busy}
                  onClick={() => run(() => api.setAvatar(session.token, p))}>
            <PresetAvatar id={p} size={52} />
          </button>
        ))}
      </div>

      {uploaded && (
        <div className="row">
          <img src={uploaded} alt="" width={52} height={52} className="avatars__mine" />
          <span className="small dim">{t('profile.yourUpload')}</span>
        </div>
      )}

      {/*
        `accept` 只是给系统文件选择器的提示，**不是校验** ——
        真正的校验在服务端按魔数做（见 account/src/account/avatars.py）。
        写在这里是为了让用户少选到一个必然被拒的文件。
      */}
      <input ref={file} type="file" hidden
             accept="image/jpeg,image/png,image/webp"
             onChange={(e) => {
               const f = e.target.files?.[0];
               e.target.value = '';     // 选同一个文件两次也要触发
               if (f) void run(() => api.uploadAvatar(session.token, f));
             }} />
      <Button variant="ghost" className="btn--wide" disabled={busy}
              onClick={() => file.current?.click()}>
        {t('profile.uploadOwn')}
      </Button>
      <p className="small dim">{t('profile.uploadHint')}</p>
    </Sheet>
  );
}
